import * as React from "react";
import InfoDialogLayout from "components/DialogLayout/InfoDialogLayout";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import repository from "client/repository";
import {TopicSubscriptionResource} from "client/resources/topicSubscriptionResource";
import {MessageBusMessageResource} from "client/resources/messageBusMessageResource";
import SimpleDataTable from "components/SimpleDataTable";
import ActionButton, {ActionButtonType} from "components/Button";
import OverflowMenu from "components/Menu/OverflowMenu";
import { Callout } from "@octopusdeploy/design-system-components";
import {FriendlyLocalTime} from "shared/FriendlyLocalTime";
import externalSystemLinks from "externalSystemLinks";
import {Number} from "shared/Number";

class MessagesDataTable extends SimpleDataTable<MessageBusMessageResource> {}

interface Props {
    subscription: TopicSubscriptionResource;
}

interface State extends DataBaseComponentState {
    messagesToPreview?: MessageBusMessageResource[];
    numberOfMessagesToPreview: number;
}

class PreviewDeadLetterQueueMessagesDialog extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { numberOfMessagesToPreview: 10 };
    }

    render() {
        const {subscription} = this.props;
        return <InfoDialogLayout title={"Preview"} errors={this.state.errors} busy={this.state.busy}>
            <Callout type="information" title="Limit">
                <p>Messages are read from the beginning of the dead letter queue.</p>
                <p>Avoid previewing more than 100 to keep the UI responsive.</p>
            </Callout>
            <div>
                <Number label="Number Of Messages To Preview"
                    value={this.state.numberOfMessagesToPreview}
                    min={0}
                    onChange={numberOfMessagesToPreview => this.setState({numberOfMessagesToPreview})}
                />
                <ActionButton type={ActionButtonType.Primary} label="Preview" onClick={() => this.previewMessages(subscription, this.state.numberOfMessagesToPreview)} />
            </div>
            {this.state.messagesToPreview && this.showMessages(subscription, this.state.messagesToPreview)}
        </InfoDialogLayout>;
    }

    private async previewMessages(subscription: TopicSubscriptionResource, numberOfMessagesToPreview: number) {
        return await this.doBusyTask(async () => {
            const messagesToPreview = await repository.MessageBus.previewDeadLetterQueueMessages(subscription, numberOfMessagesToPreview);
            this.setState({messagesToPreview});
        });
    }

    private async retryDeadLetterQueueMessage(subscription: TopicSubscriptionResource, message: MessageBusMessageResource) {
        return await this.doBusyTask(async () => {
            await repository.MessageBus.retryDeadLetterQueueMessage(subscription, message.SequenceNumber);
            await this.previewMessages(subscription, this.state.numberOfMessagesToPreview);
        });
    }

    private async deleteDeadLetterQueueMessage(subscription: TopicSubscriptionResource, message: MessageBusMessageResource): Promise<boolean> {
        return await this.doBusyTask(async () => {
            await repository.MessageBus.deleteDeadLetterQueueMessage(subscription, message.SequenceNumber);
            await this.previewMessages(subscription, this.state.numberOfMessagesToPreview);
        });
    }

    private showMessages(subscription: TopicSubscriptionResource, messagesToPreview: MessageBusMessageResource[]) {
        return <MessagesDataTable
            data={messagesToPreview}
            onRow={message => this.buildMessageRow(message)}
            onRowDetail={message => this.buildMessageBody(message)}
            onRowOverflowMenu={message => [
                OverflowMenu.externalNavItem("Logs", externalSystemLinks.seq.messageBusMessage(message)),
                OverflowMenu.item("Retry", async () => { await this.retryDeadLetterQueueMessage(subscription, message); }),
                OverflowMenu.deleteItemDefault(`${message.Type} event with ${message.MessageId} id`, () => this.deleteDeadLetterQueueMessage(subscription, message))
            ]}
            headerColumns={["Message Id", "Sequence Number", "Type", "State", "Enqueued"]}
            onEmpty={<div>No messages found</div>}
        />;
    }

    private buildMessageRow = (message: MessageBusMessageResource) => {
        return [
            message.MessageId,
            message.SequenceNumber,
            message.Type,
            message.State,
            <FriendlyLocalTime time={message.Enqueued}/>
        ];
    }

    // The message body can be large; render it full-width beneath the row (the global `pre` style
    // wraps it) so it stays inside the dialog instead of overflowing the table horizontally.
    private buildMessageBody = (message: MessageBusMessageResource) => {
        return message.Body ? <pre>{message.Body}</pre> : null;
    }
}

export default PreviewDeadLetterQueueMessagesDialog;
